import { createFileRoute, Outlet, useNavigate } from '@tanstack/react-router'
import { useEffect } from 'react'
import { DashboardLayout } from '@/components/layout/dashboard-layout'
import { useAuth } from '@workos-inc/authkit-react'
import { Loader2 } from 'lucide-react'
import { useOnboardingStatus } from '@/api'
import { useRealtimeEvents } from '@/hooks/use-realtime'

export const Route = createFileRoute('/_authenticated')({
  component: AuthenticatedLayout,
})

function AuthenticatedLayout() {
  const navigate = useNavigate()
  const { user, isLoading, signIn } = useAuth()

  // Fetch onboarding status to check if user needs onboarding
  const { data: onboardingStatus, isLoading: statusLoading } = useOnboardingStatus({
    enabled: !!user,
  })

  // SSE real-time connection (must be before any conditional returns)
  const { status: realtimeStatus } = useRealtimeEvents()

  // Redirect to onboarding if needed - must be before any conditional returns
  useEffect(() => {
    if (onboardingStatus && !onboardingStatus.has_inbox) {
      navigate({ to: '/onboarding' })
    }
  }, [onboardingStatus, navigate])

  // Trigger sign in for unauthenticated users
  useEffect(() => {
    if (!isLoading && !user) {
      signIn()
    }
  }, [isLoading, user, signIn])

  // Show loading while AuthKit initializes or checking onboarding
  if (isLoading || !user || statusLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    )
  }

  // If needs onboarding, show loading while redirecting
  if (onboardingStatus && !onboardingStatus.has_inbox) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    )
  }

  return (
    <DashboardLayout realtimeStatus={realtimeStatus}>
      <Outlet />
    </DashboardLayout>
  )
}
